import { ComponentProps, ComponentType } from 'react';

import dynamic from 'next/dynamic';

import { CommonSectionProps } from '@portal/shared/studio/types';
import { PageSectionType } from '@portal/types/studio/page';
import BenefitsTest from 'shared/studio/BenefitsTest/BenefitsTest';

const AddToCartSectionShop = dynamic(
  () => import('shared/studio/AddToCartSectionShop/AddToCartSectionShop'),
  {}
);

const SimplifiedBaB = dynamic(() => import('shared/studio/SimplifiedBaB/SimplifiedBaB'), {});

const ClinicalStudy = dynamic(() => import('shared/studio/ClinicalStudy/ClinicalStudy'));
const CenterCallOut = dynamic(() => import('shared/studio/CenterCallOut/CenterCallOut'), {});
const Ingredients = dynamic(() => import('shared/studio/Ingredients/Ingredients'));
const Quote = dynamic(() => import('shared/studio/Quote/Quote'), {});
const OfferV2BlockMarkup = dynamic(() => import('shared/studio/OfferV2/OfferV2'), {});
const OfferV3BlockMarkup = dynamic(() => import('shared/studio/OfferV3'), {});

const FaqBlockV2Markup = dynamic(() => import('shared/studio/FaqV2/FaqV2'), {});

const ReviewsAndIngredientsBlockMarkup = dynamic(
  () => import('shared/studio/ReviewsAndIngredients/ReviewsAndIngredients'),
  {}
);
const HeaderFull = dynamic(
  () => import('shared/components/Header/Header').then((comp) => comp.Header),
  {}
);
const HeaderLanderV3Markup = dynamic(
  () =>
    import('shared/studio/HeaderLanderV3/HeaderLanderV3').then((comp) => comp.HeaderLanderV3Markup),
  {}
);
const IngredientsSliderBlockMarkup = dynamic(
  () => import('shared/studio/IngredientsSlider/IngredientsSlider'),
  {}
);
const HeroFullV4 = dynamic(() => import('shared/studio/HeroFullV4/HeroFullV4'), {});
const HeroHalfV5 = dynamic(() => import('shared/studio/HeroHalfV5/HeroHalfV5'), {});
const ReferAFriend = dynamic(() => import('shared/studio/ReferAFriend/ReferAFriend'), {});
const IngredientsTabs = dynamic(() => import('shared/studio/IngredientsTabs/IngredientsTabs'), {});
const MessageImageAndTextV2 = dynamic(
  () => import('shared/studio/MessageImageAndTextV2/MessageImageAndTextV2'),
  {}
);
const BenefitsListBlockMarkup = dynamic(
  () =>
    import('shared/studio/BenefitsList/BenefitsList').then((comp) => comp.BenefitsListBlockMarkup),
  {}
);
const SplitSpecCallouts = dynamic(
  () => import('shared/studio/SplitSpecCallouts/SplitSpecCallouts'),
  {}
);
const Terms = dynamic(() => import('shared/studio/Terms/Terms').then((comp) => comp.Terms), {});
const VideoAndText = dynamic(() => import('shared/studio/VideoAndText/VideoAndText'), {});
const VideoAndCTA = dynamic(() => import('shared/studio/VideoAndCTA/VideoAndCTA'), {});
const VideoReviews = dynamic(() => import('shared/studio/VideoReviews/VideoReviews'), {});
const VideoReviewsTabbed = dynamic(
  () => import('shared/studio/VideoReviewsTabbed/VideoReviewsTabbed'),
  {}
);
const Videostrip = dynamic(() => import('shared/studio/Videostrip/Videostrip'), {});
const BenefitsJourneyV4Markup = dynamic(
  () => import('shared/studio/BenefitsJourneyV4/BenefitsJourneyV4'),
  {}
);
const BenefitsStickyTabs = dynamic(
  () => import('shared/studio/BenefitsStickyTabs/BenefitsStickyTabs'),
  {}
);
const HeroSlimBlockMarkup = dynamic(
  () => import('shared/studio/HeroSlim/HeroSlim').then((comp) => comp.HeroSlimBlockMarkup),
  {}
);
const SubscribeSection = dynamic(
  () => import('shared/studio/SubscribeSection/SubscribeSection'),
  {}
);
const TabbedCollection = dynamic(
  () => import('shared/studio/TabbedCollection/TabbedCollection'),
  {}
);
const HighlightedContent = dynamic(
  () => import('shared/studio/HighlightedContent/HighlightedContent'),
  {}
);
const SocialMedia = dynamic(() => import('shared/studio/SocialMedia/SocialMedia'), {});

const SplitContentHighlight = dynamic(
  () => import('shared/studio/SplitContentHighlight/SplitContentHighlight'),
  {}
);

const HeroCustomer = dynamic(() => import('shared/studio/HeroCustomer/HeroCustomer'), {});
const StandardsV3Markup = dynamic(
  () => import('shared/studio/StandardsV3/StandardsV3').then((comp) => comp.StandardsV3Markup),
  {}
);
const OrderTracking = dynamic(() => import('shared/studio/OrderTracking/OrderTracking'), {});
const RawHTML = dynamic(() => import('shared/studio/RawHTML/RawHTML'), {
  ssr: false
});
const MessageAndCTAV2 = dynamic(() => import('shared/studio/MessageAndCTAV2/MessageAndCTAV2'), {});
const ImageFilmstrip = dynamic(() => import('shared/studio/ImageFilmstrip/ImageFilmstrip'), {});
const TestimonialsV5 = dynamic(() => import('shared/studio/TestimonialsV5/TestimonialsV5'), {});
const ReviewsList = dynamic(
  () => import('shared/studio/ReviewsList/ReviewsList').then((comp) => comp.ReviewsList),
  {}
);
const HeroText = dynamic(() => import('shared/studio/HeroText/HeroText'), {});
const JobList = dynamic(
  () => import('shared/studio/JobList/JobList').then((comp) => comp.JobList),
  {}
);
const ImageAndTextV3 = dynamic(() => import('shared/studio/ImageAndTextV3/ImageAndTextV3'), {});
const ImageAndExpandableList = dynamic(
  () => import('shared/studio/ImageAndExpandableList/ImageAndExpandableList'),
  {}
);
const ImageAndExpandableListV2 = dynamic(
  () => import('shared/studio/ImageAndExpandableListV2/ImageAndExpandableListV2'),
  {}
);
const BenefitsV2 = dynamic(() => import('shared/studio/BenefitsV2/BenefitsV2'), {});
const CardCarousel = dynamic(() => import('shared/studio/CardCarousel/CardCarousel'), {});
const Reviews = dynamic(() => import('shared/studio/Reviews/Reviews'), {});
const Chart = dynamic(() => import('shared/studio/Chart/Chart'), {});
const AnchorNavigation = dynamic(
  () =>
    import('shared/studio/AnchorNavigation/AnchorNavigation').then(
      (comp) => comp.AnchorNavigationMarkup
    ),
  {}
);
const Offer = dynamic(() => import('shared/studio/Offer/Offer'), {});
const ContentPopup = dynamic(() => import('shared/studio/ContentPopup/ContentPopup'), {});
const Gallery = dynamic(() => import('shared/studio/Gallery/Gallery'), {});
const OfferWelcomeKit = dynamic(() => import('shared/studio/OfferWelcomeKit/OfferWelcomeKit'), {});
const ValuePropCardsV2 = dynamic(
  () => import('shared/studio/ValuePropCardsV2/ValuePropCardsV2'),
  {}
);
const HeroQuotes = dynamic(() => import('shared/studio/HeroQuotes/HeroQuotes'), {});
const BuildBundleV3 = dynamic(() => import('shared/studio/BuildBundleV3/BuildBundleV3'), {});

const ValueReviewCards = dynamic(
  () => import('shared/studio/ValueReviewCards/ValueReviewCards'),
  {}
);
const ImageAnimatedAndText = dynamic(
  () => import('shared/studio/ImageAnimatedAndText/ImageAnimatedAndText'),
  {}
);
const ImageAndTiles = dynamic(() => import('shared/studio/ImageAndTiles/ImageAndTiles'), {});
const TagsLineV2 = dynamic(() => import('shared/studio/TagsLineV2/TagsLineV2'), {});
const HeroAnimated = dynamic(() => import('shared/studio/HeroAnimated/HeroAnimated'), {});
const ImageAndIngredients = dynamic(
  () => import('shared/studio/ImageAndIngredients/ImageAndIngredients'),
  {}
);
const HeroHalfTest = dynamic(() => import('shared/studio/HeroHalfTest/HeroHalfTest'), {});
const ImageGrid = dynamic(() => import('shared/studio/ImageGrid/ImageGrid'), {});
const EmailSubscription = dynamic(() => import('shared/studio/EmailSubscription'), {});
const EmailSubscriptionPopup = dynamic(() => import('shared/studio/EmailSubscriptionPopup'), {});
const ReviewsListed = dynamic(() => import('shared/studio/ReviewsListed/index'), {});
const IngredientsList = dynamic(() => import('shared/studio/IngredientsList/IngredientsList'), {});
const ParagraphTextPage = dynamic(() => import('shared/studio/ParagraphTextPage/index'), {});
const ContentList = dynamic(
  () => import('shared/studio/ContentList/ContentList').then((comp) => comp.ContentList),
  {}
);
const Benefits = dynamic(
  () => import('shared/studio/Benefits/Benefits').then((comp) => comp.Benefits),
  {}
);
const ImageAndReviews = dynamic(() => import('shared/studio/ImageAndReviews/ImageAndReviews'), {});
const TextAndTable = dynamic(() => import('shared/studio/TextAndTable/TextAndTable'), {});
const ImageAndSliderButtons = dynamic(
  () => import('shared/studio/ImageAndSliderButtons/ImageAndSliderButtons'),
  {}
);
const SideBySideSection = dynamic(
  () => import('shared/studio/SideBySideSection/SideBySideSection'),
  {}
);
const VerticalImageAndText = dynamic(
  () => import('shared/studio/VerticalImageAndText/VerticalImageAndText'),
  {}
);
const V53Hologram = dynamic(() => import('shared/studio/V53Hologram/V53Hologram'), {});
const NewsroomFeed = dynamic(() => import('shared/studio/NewsroomFeed'), {});
const QuantitySelectorIncremental = dynamic(
  () =>
    import(
      'shared/studio/ProductDetailPage/QuantitySelectorIncremental/QuantitySelectorIncremental'
    ),
  {}
);
const QuantitySelectorCards = dynamic(
  () => import('shared/studio/ProductDetailPage/QuantitySelectorCards/QuantitySelectorCards'),
  {}
);
const ProductToggle = dynamic(
  () => import('shared/studio/ProductDetailPage/ProductToggle/ProductToggle'),
  {}
);
const PriceSelector = dynamic(
  () => import('shared/studio/ProductDetailPage/PriceSelector/PriceSelector'),
  {}
);

const CtaCard = dynamic(() => import('@portal/shared/studio/CtaCard/CtaCard'), {});
const PromoFlavorCard = dynamic(
  () => import('@portal/shared/studio/PromoFlavorCard/PromoFlavorCard'),
  {}
);

const ImageAccordion = dynamic(
  () => import('@portal/shared/studio/ImageAccordion/ImageAccordion'),
  {}
);

const CheckoutOptions = dynamic(
  () => import('shared/studio/ProductDetailPage/CheckoutOptions/CheckoutOptions'),
  {}
);
const FlavorSelector = dynamic(
  () => import('shared/studio/ProductDetailPage/FlavorSelector/FlavorSelector'),
  {}
);
const LargeVideo = dynamic(() => import('shared/studio/LargeVideo/LargeVideo'), {
  ssr: false
});
const Breadcrumbs = dynamic(
  () => import('@portal/shared/studio/ProductDetailPage/Breadcrumbs/Breadcrumbs'),
  {}
);
const ImageCarouselPDP = dynamic(
  () => import('shared/studio/ProductDetailPage/ImageCarouselPDP/ImageCarouselPDP'),
  {}
);
const StarRatings = dynamic(
  () => import('shared/studio/ProductDetailPage/StarRatings/StarRatings'),
  {}
);
const WhatsIncluded = dynamic(
  () => import('shared/studio/ProductDetailPage/WhatsIncluded/WhatsIncluded'),
  {}
);
const Headline = dynamic(() => import('shared/studio/ProductDetailPage/Headline/Headline'), {});
const FaqSimplified = dynamic(
  () => import('shared/studio/ProductDetailPage/FaqSimplified/FaqSimplified'),
  {}
);
const HsaFsaCaptions = dynamic(
  () => import('shared/studio/ProductDetailPage/HsaFsaCaptions/HsaFsaCaptions'),
  {}
);
const UnifiedButtonPDP = dynamic(
  () => import('shared/studio/ProductDetailPage/UnifiedButtonPDP/UnifiedButtonPDP'),
  {}
);
const BenefitsWithIcon = dynamic(
  () => import('shared/studio/ProductDetailPage/BenefitsWithIcon/BenefitsWithIcon'),
  {}
);

const ShopListHeader = dynamic(
  () => import('@portal/shared/studio/ShopListHeader/ShopListHeader'),
  {}
);
const ShopListNav = dynamic(() => import('@portal/shared/studio/ShopListNav/ShopListNav'), {});
const ImageAndChecklist = dynamic(
  () => import('@portal/shared/studio/ImageAndChecklist/ImageAndChecklist'),
  {}
);
const ImageContentHighlight = dynamic(
  () => import('@portal/shared/studio/ImageContentHighlight/ImageContentHighlight'),
  {}
);
const ComparisonCards = dynamic(
  () => import('@portal/shared/studio/ComparisonCards/ComparisonCards'),
  {}
);

// Note: These components are broken out just to be explicit that we're not
// server side rendering them. Otherwise, they behave exactly the same.
const ShopListBanners = dynamic(() => import('@portal/processes/ShopListBanners/ShopListBanners'), {
  ssr: false
});
const ShopListBundles = dynamic(() => import('@portal/processes/ShopListBundles/ShopListBundles'), {
  ssr: false
});
const ShopListProducts = dynamic(
  () => import('@portal/processes/ShopListProducts/ShopListProducts'),
  {
    ssr: false
  }
);

export const componentMap = {
  // These are deprecated and no longer work.
  headerFull: undefined,
  subAndSaveBaB: undefined,
  offerBaB: undefined,
  buildBundle: undefined,
  buildBundleV2: undefined,
  buildBundle3Months: undefined,
  addToCartSection: undefined,
  hero: undefined,

  addToCartSectionShop: AddToCartSectionShop,
  quoteLander: Quote,
  offer: Offer,
  offerV2: OfferV2BlockMarkup,
  offerV3: OfferV3BlockMarkup,
  cardCarousel: CardCarousel,
  reviews: Reviews,
  faqV2: FaqBlockV2Markup,
  benefitsV2: BenefitsV2,
  benefitsStickyTabs: BenefitsStickyTabs,
  standardsV3: StandardsV3Markup,
  reviewsAndIngredients: ReviewsAndIngredientsBlockMarkup,
  heroFullV4: HeroFullV4,
  heroHalfV5: HeroHalfV5,
  headerLanderV3: HeaderLanderV3Markup,
  ingredientsSlider: IngredientsSliderBlockMarkup,
  messageImageAndTextV2: MessageImageAndTextV2,
  imageAndTextV3: ImageAndTextV3,
  videoAndText: VideoAndText,
  videoAndCTA: VideoAndCTA,
  videoReviews: VideoReviews,
  videoReviewsTabbed: VideoReviewsTabbed,
  videostrip: Videostrip,
  benefitsList: BenefitsListBlockMarkup,
  splitSpecCallouts: SplitSpecCallouts,
  terms: Terms,
  heroSlim: HeroSlimBlockMarkup,
  subscribeSection: SubscribeSection,
  tabbedCollection: TabbedCollection,
  headerFullV2: HeaderFull,
  highlightedContent: HighlightedContent,
  socialMedia: SocialMedia,
  splitContentHighlight: SplitContentHighlight,
  simplifiedBaB: SimplifiedBaB,
  messageAndCTAV2: MessageAndCTAV2,
  orderTracking: OrderTracking,
  imageFilmstrip: ImageFilmstrip,
  referAFriend: ReferAFriend,
  reviewsList: ReviewsList,
  heroCustomer: HeroCustomer,
  heroText: HeroText,
  jobList: JobList,
  imageAndExpandableList: ImageAndExpandableList,
  imageAndExpandableListV2: ImageAndExpandableListV2,
  chart: Chart,
  anchorNavigation: AnchorNavigation,
  benefitsJourneyV4: BenefitsJourneyV4Markup,
  ingredientsTabs: IngredientsTabs,
  contentPopup: ContentPopup,
  testimonialsV5: TestimonialsV5,
  gallery: Gallery,
  offerWelcomeKit: OfferWelcomeKit,
  valuePropCardsV2: ValuePropCardsV2,
  rawHTML: RawHTML,
  valueReviewCards: ValueReviewCards,
  heroQuotes: HeroQuotes,
  imageAnimatedAndText: ImageAnimatedAndText,
  imageAndTiles: ImageAndTiles,
  tagsLineV2: TagsLineV2,
  imageAndIngredients: ImageAndIngredients,
  heroAnimated: HeroAnimated,
  heroHalfTest: HeroHalfTest,
  benefitsTest: BenefitsTest,
  buildBundleV3: BuildBundleV3,
  emailSubscription: EmailSubscription,
  emailSubscriptionPopup: EmailSubscriptionPopup,
  reviewsListed: ReviewsListed,
  ingredientsList: IngredientsList,
  paragraphTextPage: ParagraphTextPage,
  contentList: ContentList,
  clinicalStudy: ClinicalStudy,
  centerCallOut: CenterCallOut,
  ingredients: Ingredients,
  benefits: Benefits,
  imageGrid: ImageGrid,
  imageAndReviews: ImageAndReviews,
  textAndTable: TextAndTable,
  imageAndSliderButtons: ImageAndSliderButtons,
  sideBySideSection: SideBySideSection,
  verticalImageAndText: VerticalImageAndText,
  v53Hologram: V53Hologram,
  newsroomFeed: NewsroomFeed,
  quantitySelectorIncremental: QuantitySelectorIncremental,
  quantitySelectorCards: QuantitySelectorCards,
  checkoutOptions: CheckoutOptions,
  flavorSelector: FlavorSelector,
  priceSelector: PriceSelector,
  ctaCard: CtaCard,
  imageAccordion: ImageAccordion,
  promoFlavorCard: PromoFlavorCard,
  breadcrumbs: Breadcrumbs,
  starRatings: StarRatings,
  whatsIncluded: WhatsIncluded,
  imageCarouselPDP: ImageCarouselPDP,
  headline: Headline,
  faqSimplified: FaqSimplified,
  hsaFsaCaptions: HsaFsaCaptions,
  unifiedButtonPDP: UnifiedButtonPDP,
  benefitsWithIcon: BenefitsWithIcon,
  shopV3Header: ShopListHeader,
  shopV3Nav: ShopListNav,
  shopV3Banners: ShopListBanners,
  shopV3Bundles: ShopListBundles,
  shopV3Products: ShopListProducts,
  productToggle: ProductToggle,
  imageAndChecklist: ImageAndChecklist,
  imageContentHighlight: ImageContentHighlight,
  largeVideo: LargeVideo,
  comparisonCards: ComparisonCards
} as const satisfies Record<PageSectionType, ComponentType<CommonSectionProps> | undefined>;

export type ComponentMapType = keyof typeof componentMap;

export const getComponent = <T extends ComponentMapType>(type: T) => {
  // We don't want the return type to be `ComponentType<Props> |
  // ComponentType<Props>` otherwise typescript won't know how to target each
  // comoponent when passing props into it. It ends up behaving more like
  // ComponentType<Props & Props>, which is incorrect.
  //
  // The silly fix is to unwrap into a props union first, and then rewrap into
  // the component type so that it looks more like `ComponentType<Props |
  // Props>`.
  type MappedComponentType = (typeof componentMap)[T];
  type RewrappedComponentType = MappedComponentType extends ComponentType
    ? ComponentType<ComponentProps<MappedComponentType>>
    : undefined;
  return componentMap[type] as RewrappedComponentType;
};

/**
 * This is a list of sections that take up a very slim amount of space. We use
 * this to try to guess at how many sections are visible on the page when
 * loading. Generally they should be a quarter of the viewport or less in
 * height.
 */
export const SlimSectionTypes = [
  'anchorNavigation',
  'breadcrumbs',
  'centerCallOut',
  'headerFullV2',
  'headerLanderV3',
  'headline',
  'heroSlim',
  'hsaFsaCaptions',
  'messageAndCTAV2',
  'shopV3Header',
  'shopV3Nav',
  'tagsLineV2',
  'unifiedButtonPDP'
] as const satisfies PageSectionType[];
